import type { Dispatch as ReactDispatch } from 'react';
import type { Middleware } from 'redux';
import * as ActionTypes from '../actions';

// ECDEX-3441 (MR B, reshaped in MR F): the bridge from Redux actions to the
// AuthProvider Context. Auth state lives only in the Context. The ~20 action
// creators in candidateLogin.js / workosLogin.ts and the report thunks in
// reports.js still dispatch plain actions into the store (the report data
// reducers in portal.js handle many of the same actions), so this forwards
// every action that candidateLogin.reducer.js or portalAuth.reducer.js handles
// to the Context. Both reducers now run only in the Context.
//
// Removed when the report thunks and login flows no longer dispatch through
// the store (ECDEX-3840 and the final Redux teardown, ECDEX-3443).
//
// Do not call the useAuth() actions (MRs A2-A4) from a component while this
// is active: they dispatch to the Context directly, so the same transition
// would be applied twice.
//
// triggerAuthLogout() (authLogoutTrigger.ts) is safe to call: AuthProvider
// registers a logout that dispatches to the Redux store, which this mirror
// forwards to the Context.
//
// sessionStorage side effect: LOGOUT_CANDIDATE, FETCH_REPORTS_FAILURE, FETCH_REPORTS_UNKNOWN_FAILURE,
// FETCH_REPORTS_FAILURE_INTL and FETCH_REPORTS_BUSY clear sessionStorage. That
// used to happen inside candidateLogin.reducer.js on the Redux copy of the
// reducer, synchronously at dispatch. The reducer now runs only in the
// Context, where React may call it later than the dispatch and more than once
// (StrictMode), so the clear lives here instead: once per dispatched action,
// synchronously, after the Redux reducers (so after portal.js's own
// LOGOUT_CANDIDATE write) and before the Context sees the action. The
// reducer stays pure.

let contextDispatch: ReactDispatch<any> | null = null;

export const setAuthContextDispatch = (next: ReactDispatch<any> | null) => {
  contextDispatch = next;
};

// Unregisters `dispatch` only if it is still the registered one, so an
// unmounting provider can't clobber a provider that registered after it.
export const clearAuthContextDispatch = (dispatch: ReactDispatch<any>) => {
  if (contextDispatch === dispatch) contextDispatch = null;
};

// Built lazily: actions/ and the store import each other, so the action-type
// constants are not safe to read at module-evaluation time.
let mirroredTypes: Set<string> | null = null;

const typesToMirror = (): Set<string> => {
  mirroredTypes ??= new Set<string>([
    // candidateLogin.reducer.js
    ActionTypes.CHECK_FOR_ACTIVE_AUTH,
    ActionTypes.CHECK_FOR_ACTIVE_AUTH_COMPLETE,
    ActionTypes.LOGOUT_CANDIDATE,
    ActionTypes.WORKOS_RESET,
    ActionTypes.WORKOS_MAGIC_CODE_SENDING,
    ActionTypes.WORKOS_MAGIC_CODE_SENT,
    ActionTypes.WORKOS_VERIFY_REQUEST,
    ActionTypes.WORKOS_VERIFY_SUCCESS,
    ActionTypes.WORKOS_VERIFY_FAILURE,
    ActionTypes.WORKOS_DOB_REQUIRED,
    // candidateLogin.reducer.js and portalAuth.reducer.js
    ActionTypes.FETCH_REPORTS_FAILURE,
    ActionTypes.FETCH_REPORTS_UNKNOWN_FAILURE,
    ActionTypes.FETCH_REPORTS_FAILURE_INTL,
    ActionTypes.FETCH_REPORTS_BUSY,
    ActionTypes.CLEAR_FETCH_REPORTS_FAILURE,
    // portalAuth.reducer.js
    ActionTypes.FETCH_REPORTS_REQUEST,
    ActionTypes.FETCH_REPORTS_SUCCESS,
    ActionTypes.FETCH_REPORTS_REDIRECT,
    ActionTypes.CLEAR_LOGGED_OUT,
    ActionTypes.PORTAL_RESET,
    ActionTypes.NON_CANDIDATE_LOGGED_IN,
  ]);
  return mirroredTypes;
};

export const getMirroredAuthActionTypes = (): ReadonlySet<string> =>
  typesToMirror();

const clearsSessionStorage = (type: string | undefined): boolean =>
  type === ActionTypes.LOGOUT_CANDIDATE ||
  type === ActionTypes.FETCH_REPORTS_FAILURE ||
  type === ActionTypes.FETCH_REPORTS_UNKNOWN_FAILURE ||
  type === ActionTypes.FETCH_REPORTS_FAILURE_INTL ||
  type === ActionTypes.FETCH_REPORTS_BUSY;

// Runs after the Redux reducers so the two stores see an action in the same
// order. The session clear runs whether or not a provider has registered; the
// forward is a no-op until AuthProvider registers its dispatch (tests, SSG).
export const authContextMirror: Middleware = () => next => action => {
  const result = next(action);
  if (clearsSessionStorage(action?.type)) window.sessionStorage.clear();
  if (contextDispatch && typesToMirror().has(action?.type)) {
    contextDispatch(action);
  }
  return result;
};
